// Catalogo dei servizi dell'area clienti: SERVIZIO (uno dei 6 riquadri) → CATEGORIA → VOCE.
// Ogni voce è una pratica con la sua scheda: descrizione, documenti da portare, tempi, costo.
// Il cliente la vede su clienti.crm-jv.com toccando il riquadro del servizio (anche senza accesso).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Copy, Eye, EyeOff, ArrowUp, ArrowDown, X, FileText, Settings2, Sparkles, Loader2, ExternalLink } from 'lucide-react';
import GestioneServizi from '../components/areaClienti/GestioneServizi';
import CampiCinese from '../components/areaClienti/CampiCinese';
import CreaConAi, { chiediAi } from '../components/areaClienti/CreaConAi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio`;
const vuota = (servizio, categoria = '') => ({
  servizio, categoria, titolo: '', sommario: '', descrizione: '', documenti: [{ nome: '', nota: '', obbligatorio: true }],
  tempi: '', costo: '', ordine: 0, pubblicata: false, prenotazione: '', fonti_ai: [],
});
const campiVoce = (v) => ({
  servizio: v.servizio, categoria: v.categoria || '', titolo: v.titolo, sommario: v.sommario || '', descrizione: v.descrizione || '',
  documenti: v.documenti || [], tempi: v.tempi || '', costo: v.costo || '', ordine: v.ordine || 0, pubblicata: !!v.pubblicata,
  prenotazione: v.prenotazione_scelta || '',
  titolo_zh: v.titolo_zh || '', categoria_zh: v.categoria_zh || '', sommario_zh: v.sommario_zh || '', descrizione_zh: v.descrizione_zh || '',
  tempi_zh: v.tempi_zh || '', costo_zh: v.costo_zh || '', fonti_ai: v.fonti_ai || [],
});

export default function CatalogoClienti() {
  const [dati, setDati] = useState({ servizi: [], voci: [] });
  const [servizio, setServizio] = useState('Permessi di soggiorno');
  const [form, setForm] = useState(null);
  const [idInModifica, setIdInModifica] = useState(null);
  const [salvo, setSalvo] = useState(false);
  const [gestione, setGestione] = useState(false);
  const [aiAperta, setAiAperta] = useState(false);
  const [ai, setAi] = useState({ istruzioni: '', web: true, lavoro: false, fonti: [], avviso: '' });

  const carica = useCallback(async () => {
    try { setDati((await axios.get(`${API}/catalogo`)).data); } catch (_) { toast.error('Errore nel caricamento del catalogo'); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const delServizio = useMemo(() => dati.voci.filter((v) => v.servizio === servizio), [dati, servizio]);
  const categorie = useMemo(() => {
    const out = [];
    delServizio.forEach((v) => {
      let c = out.find((x) => x.nome === (v.categoria || ''));
      if (!c) { c = { nome: v.categoria || '', voci: [] }; out.push(c); }
      c.voci.push(v);
    });
    return out;
  }, [delServizio]);
  const nomiCategorie = useMemo(() => [...new Set(dati.voci.filter((v) => v.servizio === (form?.servizio || servizio)).map((v) => v.categoria).filter(Boolean))], [dati, form, servizio]);

  const nuova = (categoria = '') => {
    const ultimo = Math.max(0, ...delServizio.map((v) => v.ordine || 0));
    setAi((x) => ({ ...x, fonti: [], avviso: '' })); setForm({ ...vuota(servizio, categoria), ordine: ultimo + 10 }); setIdInModifica(null);
  };
  const modifica = (v) => { setAi((x) => ({ ...x, fonti: (v.fonti_ai || []).map((u) => ({ url: u, titolo: u })), avviso: '' })); setForm({ ...campiVoce(v), documenti: v.documenti?.length ? v.documenti.map((d) => ({ ...d })) : [{ nome: '', nota: '', obbligatorio: true }] }); setIdInModifica(v.id); };
  const salva = async () => {
    setSalvo(true);
    try {
      const corpo = { ...form, documenti: form.documenti.filter((d) => d.nome.trim()) };
      if (idInModifica) await axios.put(`${API}/catalogo/${idInModifica}`, corpo); else await axios.post(`${API}/catalogo`, corpo);
      toast.success(form.pubblicata ? 'Salvata: i clienti la vedono' : 'Salvata in bozza (i clienti non la vedono)');
      setForm(null); carica();
    } catch (err) { toast.error(err.response?.data?.detail || 'Salvataggio non riuscito'); } finally { setSalvo(false); }
  };
  const rapido = async (v, campi) => {
    try { await axios.put(`${API}/catalogo/${v.id}`, { ...campiVoce(v), ...campi }); carica(); } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const sposta = async (v, verso) => {
    // scambia l'ordine con la voce vicina della stessa categoria
    const lista = categorie.find((c) => c.nome === (v.categoria || '')).voci;
    const i = lista.findIndex((x) => x.id === v.id);
    const altro = lista[i + verso];
    if (!altro) return;
    const [a, b] = [v.ordine || 0, altro.ordine || 0];
    try {
      await axios.put(`${API}/catalogo/${v.id}`, { ...campiVoce(v), ordine: a === b ? b + verso : b });
      await axios.put(`${API}/catalogo/${altro.id}`, { ...campiVoce(altro), ordine: a });
      carica();
    } catch (_) { toast.error('Errore'); }
  };
  const duplica = async (v) => {
    try { await axios.post(`${API}/catalogo/${v.id}/duplica`); toast.success('Copia creata in bozza: modificala'); carica(); } catch (_) { toast.error('Errore'); }
  };
  const elimina = async (v) => {
    if (!window.confirm(`Eliminare "${v.titolo}" dal catalogo?`)) return;
    try { await axios.delete(`${API}/catalogo/${v.id}`); toast.success('Eliminata'); carica(); } catch (_) { toast.error('Errore'); }
  };
  // «Scrivi con AI» dentro la scheda: riempie i campi, poi lo studio rilegge e salva
  const scriviConAi = async () => {
    setAi((x) => ({ ...x, lavoro: true, avviso: '' }));
    try {
      const conTesto = form.descrizione.trim() || form.documenti.some((d) => d.nome.trim());
      const p = await chiediAi({ servizio: form.servizio, categoria: form.categoria, titolo: form.titolo, istruzioni: ai.istruzioni, web: ai.web,
        attuale: conTesto ? { ...form, documenti: form.documenti.filter((d) => d.nome.trim()) } : null });
      setForm((f) => ({ ...f, sommario: p.sommario || f.sommario, descrizione: p.descrizione || f.descrizione,
        documenti: p.documenti.length ? p.documenti : f.documenti, tempi: p.tempi || f.tempi, costo: p.costo || f.costo,
        fonti_ai: p.fonti.map((x) => x.url) }));
      setAi((x) => ({ ...x, lavoro: false, fonti: p.fonti, avviso: p.avviso }));
      toast.success("Scheda scritta dall'AI: rileggila e correggi prima di salvare");
    } catch (err) { setAi((x) => ({ ...x, lavoro: false })); toast.error(err.message); }
  };
  const doc = (i, campi) => setForm({ ...form, documenti: form.documenti.map((d, j) => (j === i ? { ...d, ...campi } : d)) });
  const spostaDoc = (i, verso) => {
    const l = [...form.documenti]; const j = i + verso;
    if (j < 0 || j >= l.length) return;
    [l[i], l[j]] = [l[j], l[i]]; setForm({ ...form, documenti: l });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Catalogo servizi (area clienti)</h1>
          <p className="text-slate-600 mt-1">Servizio → categoria → pratica, con i documenti da portare. Visibile su clienti.crm-jv.com toccando il riquadro del servizio.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setGestione(true)}><Settings2 className="w-4 h-4 mr-2" /> Gestisci servizi</Button>
          <Button variant="outline" onClick={() => setAiAperta(true)}><Sparkles className="w-4 h-4 mr-2 text-violet-600" /> Crea con AI</Button>
          <Button onClick={() => nuova()}><Plus className="w-4 h-4 mr-2" /> Nuova pratica</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {dati.servizi.map((s) => {
          const n = dati.voci.filter((v) => v.servizio === s);
          return (
            <Button key={s} size="sm" variant={servizio === s ? 'default' : 'outline'} onClick={() => setServizio(s)}>
              {s} <span className="ml-2 text-xs opacity-70">{n.filter((v) => v.pubblicata).length}/{n.length}</span>
            </Button>
          );
        })}
      </div>

      {dati.servizi.length > 0 && !dati.servizi.includes(servizio) && (
        <Card><CardContent className="p-4 text-slate-500 text-sm">Scegli un servizio qui sopra. Nel catalogo compaiono solo i servizi con "riquadro app" attivo (Gestisci servizi).</CardContent></Card>
      )}
      {categorie.length === 0 && dati.servizi.includes(servizio) && <Card><CardContent className="p-6 text-slate-500">Nessuna pratica per {servizio}. Aggiungi la prima con "Nuova pratica".</CardContent></Card>}
      {categorie.map((c) => (
        <div key={c.nome || '-'} className="space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-800">{c.nome || 'Senza categoria'}</h2>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => nuova(c.nome)}><Plus className="w-3.5 h-3.5 mr-1" /> pratica in questa categoria</Button>
          </div>
          {c.voci.map((v, i) => (
            <Card key={v.id} className={v.pubblicata ? '' : 'opacity-75 border-dashed'}>
              <CardContent className="p-4 flex gap-4 items-start">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="font-semibold text-slate-900">{v.titolo}</span>
                    {v.pubblicata ? <Badge className="bg-emerald-100 text-emerald-800">Pubblicata</Badge> : <Badge className="bg-slate-100 text-slate-700">Bozza</Badge>}
                    <Badge variant="outline" className="gap-1"><FileText className="w-3 h-3" /> {v.documenti.length} documenti</Badge>
                  </div>
                  {v.sommario && <div className="text-sm text-slate-600 mt-0.5">{v.sommario}</div>}
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{v.documenti.map((d) => d.nome).join(' · ')}</div>
                  <div className="text-xs text-slate-400 mt-1">{v.autore}{v.aggiornata ? ` · ${v.aggiornata}` : ''}</div>
                </div>
                <div className="flex flex-wrap gap-1 justify-end w-40">
                  <Button size="sm" variant="outline" title="Su" disabled={i === 0} onClick={() => sposta(v, -1)}><ArrowUp className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" title="Giù" disabled={i === c.voci.length - 1} onClick={() => sposta(v, 1)}><ArrowDown className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" title="Modifica" onClick={() => modifica(v)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" title={v.pubblicata ? 'Togli dalla pubblicazione' : 'Pubblica'} onClick={() => rapido(v, { pubblicata: !v.pubblicata })}>
                    {v.pubblicata ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
                  <Button size="sm" variant="outline" title="Duplica (per una variante)" onClick={() => duplica(v)}><Copy className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" title="Elimina" className="text-red-600" onClick={() => elimina(v)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ))}

      <CreaConAi aperta={aiAperta} onChiudi={() => setAiAperta(false)} servizi={dati.servizi} servizio={servizio}
        voci={dati.voci} onCreate={carica} />
      <GestioneServizi aperta={gestione} onChiudi={() => setGestione(false)} onCambiato={carica} />
      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle>{idInModifica ? 'Modifica pratica' : 'Nuova pratica'}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3 min-w-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Servizio</Label>
                  <select className="w-full mt-1 h-10 rounded-md border px-3 text-sm" value={form.servizio} onChange={(e) => setForm({ ...form, servizio: e.target.value })}>
                    {dati.servizi.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Categoria (sotto-catalogo)</Label>
                  <Input list="categorie-catalogo" value={form.categoria} maxLength={80} placeholder="es. Permesso per lavoro"
                    onChange={(e) => setForm({ ...form, categoria: e.target.value })} />
                  <datalist id="categorie-catalogo">{nomiCategorie.map((c) => <option key={c} value={c} />)}</datalist>
                </div>
              </div>
              <div>
                <Label>Titolo della pratica</Label>
                <Input value={form.titolo} maxLength={120} placeholder="es. Rinnovo per lavoro subordinato" onChange={(e) => setForm({ ...form, titolo: e.target.value })} />
              </div>
              <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" onClick={scriviConAi} disabled={ai.lavoro || form.titolo.trim().length < 3}
                    className="bg-violet-600 hover:bg-violet-700">
                    {ai.lavoro ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    {ai.lavoro ? "L'AI sta scrivendo… (fino a 1 minuto)" : (form.descrizione.trim() ? 'Migliora con AI' : 'Scrivi con AI')}
                  </Button>
                  <label className="flex items-center gap-1 text-xs text-slate-600" title="Sul web va solo il titolo del servizio, mai dati dei clienti">
                    <input type="checkbox" checked={ai.web} onChange={(e) => setAi({ ...ai, web: e.target.checked })} /> cerca sul web (leggi e regole aggiornate)
                  </label>
                </div>
                <Input value={ai.istruzioni} maxLength={1500} onChange={(e) => setAi({ ...ai, istruzioni: e.target.value })}
                  placeholder="Istruzioni per l'AI (facoltative), es. «più corta», «aggiungi i documenti per i minori»" />
                {ai.avviso && <p className="text-xs text-amber-700">{ai.avviso}</p>}
                {ai.fonti.length > 0 && (
                  <div className="text-xs text-slate-600 min-w-0">Fonti usate dall'AI (controlla prima di pubblicare):
                    {ai.fonti.map((f) => <a key={f.url} href={f.url} target="_blank" rel="noreferrer" title={f.url}
                      className="flex items-center gap-1 min-w-0 text-violet-700 hover:underline">
                      <ExternalLink className="w-3 h-3 shrink-0" /><span className="truncate">{f.titolo === f.url ? f.url.replace(/^https?:\/\/(www\.)?/, '') : f.titolo}</span></a>)}
                  </div>
                )}
              </div>
              <div>
                <Label>In breve (si vede nell'elenco)</Label>
                <Input value={form.sommario} maxLength={300} placeholder="es. Per chi lavora con un contratto da dipendente" onChange={(e) => setForm({ ...form, sommario: e.target.value })} />
              </div>
              <div>
                <Label>Descrizione (facoltativa)</Label>
                <Textarea rows={4} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                  placeholder={'Cosa facciamo, come funziona.\n- Le righe con il trattino\n- diventano un elenco'} />
              </div>
              <div>
                <Label>Documenti da portare</Label>
                <div className="mt-1 space-y-2">
                  {form.documenti.map((d, i) => (
                    <div key={i} className="flex gap-2 items-start rounded-lg border p-2">
                      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Input value={d.nome} maxLength={200} placeholder="Documento (es. Passaporto valido)" onChange={(e) => doc(i, { nome: e.target.value })} />
                        <Input value={d.nota} maxLength={400} placeholder="Nota (es. copia di tutte le pagine)" onChange={(e) => doc(i, { nota: e.target.value })} />
                      </div>
                      <label className="flex items-center gap-1 text-xs whitespace-nowrap pt-2.5" title="Se non spuntato: 'solo se ricorre il caso'">
                        <input type="checkbox" checked={d.obbligatorio} onChange={(e) => doc(i, { obbligatorio: e.target.checked })} /> sempre
                      </label>
                      <Button type="button" size="sm" variant="ghost" className="px-1.5" onClick={() => spostaDoc(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                      <Button type="button" size="sm" variant="ghost" className="px-1.5" onClick={() => spostaDoc(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                      <Button type="button" size="sm" variant="ghost" className="px-1.5 text-red-600" onClick={() => setForm({ ...form, documenti: form.documenti.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, documenti: [...form.documenti, { nome: '', nota: '', obbligatorio: true }] })}>
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi documento</Button>
                  <p className="text-xs text-slate-500">"sempre" = documento obbligatorio; senza spunta va sotto "Solo se ricorre il caso".</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Tempi (facoltativo)</Label><Input value={form.tempi} maxLength={200} placeholder="es. In giornata" onChange={(e) => setForm({ ...form, tempi: e.target.value })} /></div>
                <div><Label>Costo (facoltativo)</Label><Input value={form.costo} maxLength={200} placeholder="es. Chiedi il preventivo" onChange={(e) => setForm({ ...form, costo: e.target.value })} /></div>
              </div>
              <CampiCinese form={form} setForm={setForm}
                campi={[['titolo', 'Titolo'], ['categoria', 'Categoria'], ['sommario', 'In breve'], ['descrizione', 'Descrizione', true], ['tempi', 'Tempi'], ['costo', 'Costo']]} />
              {form.documenti.some((d) => d.nome_zh) && (
                <p className="text-xs text-slate-500">Documenti in cinese: {form.documenti.filter((d) => d.nome).map((d) => d.nome_zh || '—').join(' · ')} (si ritraducono da soli se cambi l'italiano)</p>
              )}
              <div>
                <Label>Prenotazione appuntamento nell'app</Label>
                <select className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-white" value={form.prenotazione} onChange={(e) => setForm({ ...form, prenotazione: e.target.value })}>
                  <option value="">Automatica (dal titolo: ISEE / 730 / 740) o nessuna</option>
                  <option value="isee">ISEE: calendario per prenotare l'appuntamento ISEE</option>
                  <option value="730">730/740: calendario per prenotare l'appuntamento 730</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pubblicata}
                onChange={(e) => setForm({ ...form, pubblicata: e.target.checked })} /> Pubblicata (visibile ai clienti)</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Annulla</Button>
            <Button onClick={salva} disabled={salvo || !form || form.titolo.trim().length < 3}>{salvo ? 'Salvo…' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
